import React from "react";
import { ListItemHoverEvent, LocationTitleIcons, LocationTextLine, ServiceOption, AcceptsVouchersIndicator } from "../shared";
import {
  StyledCardLink,
  StyledCardTitle,
  StyledContent,
  StyledDetailSpan,
  StyledLabelSpan,
  StyledLocationCard,
  StyledPhoneLink,
  StyledServices,
  StyledWebsiteLink,
} from "./LocationCard-styled";
import { LocationCardProps } from "./LocationCard.types";

import { I18nextProvider, useTranslation } from "react-i18next";
import instance from "../../utils/i18n";

import { faChevronRight } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { v4 as uuidv4 } from "uuid";

import Graphic from "@arcgis/core/Graphic";
import { CalciteButton, CalciteIcon } from "@esri/calcite-components-react";
import "@esri/calcite-components/dist/components/calcite-button";

import LocationBadges from "../LocationBadge/LocationBadge";
import { getLocationBadgePropsFromCardDetails } from "../LocationBadge/getLocationBadgeProps";

const LocationCard = ({
  cardType,
  cardDetails,
  directionsLink,
  locObjId,
  location,
  hoverHandler,
  onClick,
}: LocationCardProps) => {
  const { t, i18n } = useTranslation("components", { i18n: instance });

  const cardElementType = cardType === "popup" ? "div" : "li";
  const statusColor = (open: boolean) => {
    return open ? "var(--lib-primary-green)" : "var(--lib-primary-red)";
  };

  const raiseHover = (isHovering: boolean, itemKey: string, location: Graphic): void => {
    const e: ListItemHoverEvent = {
      isHovering: isHovering,
      itemKey: itemKey,
      location: location,
    };

    if (hoverHandler) hoverHandler(e);
  };

  const badgeProps = getLocationBadgePropsFromCardDetails(
    cardDetails,
    location.getAttribute("COST")
  );
  // Home-based and School-based colors to be added when locType data is available
  const settingStyles: Record<string, {bg: string; text: string}> = {
    "Center": {bg: "#8D3AC5", text: "white"},
    "Home": {bg: "#3E8551", text: "white"},
    "School": {bg: "#FC8C38", text: "black"}
  };

  const styleConfig = cardDetails.locType
    ? settingStyles[cardDetails.locType as keyof typeof settingStyles] ?? {
        bg: "var(--lib-primary-green)",
        text: "black",
      }
    : undefined;

  return (
    <I18nextProvider i18n={i18n}>
      {/* return as li when rendered in list, div when rendered in popup */}
      <StyledLocationCard
        as={cardElementType}
        key={cardDetails.locFID}
        data-locationid={cardDetails.locFID}
        onMouseEnter={() => {
          raiseHover(true, cardDetails.locFID, location);
        }}
        onMouseLeave={() => {
          raiseHover(false, cardDetails.locFID, location);
        }}
        onKeyPress={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" && locObjId) {
            onClick(locObjId);
          }
        }}
        aria-labelledby={`cardLink_${locObjId}`}
      >
        <StyledContent>
          <div className="card-header">
            <StyledCardTitle className="card-title">
              {locObjId ? (
                <StyledCardLink
                  id={`cardLink_${locObjId}`}
                  title={t("locCard.viewDetailsFor", { locName: cardDetails.locTitle })}
                  aria-label={t("locCard.viewDetailsFor", { locName: cardDetails.locTitle })}
                  onClick={() => onClick(locObjId)}
                  tabIndex={0}
                >
                  {cardDetails.locTitle}
                </StyledCardLink>
              ) : (
                cardDetails.locTitle
              )}
            </StyledCardTitle>
            <AcceptsVouchersIndicator acceptsVouchers={cardDetails.locAcceptsVouchers} />
            <LocationBadges locType={badgeProps.locType} costType={badgeProps.costType} careType={badgeProps.careType} />
            <LocationTextLine dir="ltr">  
              {cardDetails.locAddress && (
                <StyledDetailSpan>{cardDetails.locAddress}</StyledDetailSpan>
              )}
              {cardDetails.locAddress2 && (
                <StyledDetailSpan>{cardDetails.locAddress2}</StyledDetailSpan>
              )}
           
              {cardDetails.locAddress && (
                <StyledDetailSpan>{location.getAttribute("CITY")},  {location.getAttribute("STATE")} {location.getAttribute("ZIPCODE")}</StyledDetailSpan>
              )}
            </LocationTextLine>
            <LocationTextLine>
              {cardDetails.locDist && <StyledDetailSpan>
                <CalciteIcon 
                  icon="pin-tear-f" 
                  scale="m" 
                  style={{
                      "--calcite-ui-icon-color": "red",
                      verticalAlign: "middle",
                      marginLeft: "-6px"
                  } as React.CSSProperties}
                />
                 {cardDetails.locDist} away</StyledDetailSpan>}
            </LocationTextLine>
            <LocationTextLine>
              <StyledDetailSpan><b>Ages</b> {location.getAttribute("AGEMIN_YEAR")} to {location.getAttribute("AGEMAX_YEAR")}</StyledDetailSpan>
            </LocationTextLine>            
          </div>
        </StyledContent>
      </StyledLocationCard>
    </I18nextProvider>
  );
};

export default LocationCard;
